import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Trophy, Heart, Lock, Check, Users, Target, ChevronDown, Shield, DollarSign, Share2, Copy, Gift, Star } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROUND_NAMES = { 2: 'Round of 64', 3: 'Round of 32', 4: 'Sweet 16', 5: 'Elite 8', 6: 'Final Four', 7: 'Championship' };
const ROUND_POINTS = { 2: 1, 3: 2, 4: 4, 5: 8, 6: 16, 7: 32 };
const ADMIN_EMAIL = 'admin@primestatlab.com';

// Build a map of game -> next game so picks cascade forward
function buildFlowMap(games) {
  const map = {};
  const lookup = {};
  games.forEach(g => { lookup[`${g.round_num}-${g.region}-${g.position}`] = g; });

  games.forEach(g => {
    if (g.round_num >= 2 && g.round_num <= 4) {
      // Regional: R2->R3->R4->R5
      const next = lookup[`${g.round_num + 1}-${g.region}-${Math.floor(g.position / 2)}`];
      if (next) map[g.game_id] = { nextId: next.game_id, slot: g.position % 2 === 0 ? 1 : 2 };
    }
    if (g.round_num === 5) {
      // Elite 8 -> Final Four
      const ffPos = (g.region === 'East' || g.region === 'West') ? 0 : 1;
      const slot = (g.region === 'East' || g.region === 'South') ? 1 : 2;
      const next = lookup[`6-Final Four-${ffPos}`];
      if (next) map[g.game_id] = { nextId: next.game_id, slot };
    }
    if (g.round_num === 6) {
      // Final Four -> Championship
      const next = lookup['7-Final Four-0'];
      if (next) map[g.game_id] = { nextId: next.game_id, slot: g.position === 0 ? 1 : 2 };
    }
  });
  return map;
}

// Get what teams should appear in a game based on user picks cascading forward
function getVirtualTeams(game, userPicks, flowMap) {
  let t1 = { name: game.team1_name, seed: game.team1_seed };
  let t2 = { name: game.team2_name, seed: game.team2_seed };

  // Find which games feed INTO this game
  Object.entries(flowMap).forEach(([feederId, { nextId, slot }]) => {
    if (nextId === game.game_id) {
      const pick = userPicks[feederId];
      if (pick) {
        if (slot === 1) t1 = { name: pick.team, seed: pick.seed };
        else t2 = { name: pick.team, seed: pick.seed };
      }
    }
  });
  return { t1, t2 };
}

// Clear all downstream picks when a user changes their pick
function clearDownstream(gameId, picks, flowMap) {
  const next = flowMap[gameId];
  if (next) {
    delete picks[next.nextId];
    clearDownstream(next.nextId, picks, flowMap);
  }
}

export default function Bracket() {
  const [tournament, setTournament] = useState(null);
  const [userPicks, setUserPicks] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({ total_brackets: 0, games_complete: 0, total_games: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeRegion, setActiveRegion] = useState('East');
  const [showAdmin, setShowAdmin] = useState(false);
  const [tipping, setTipping] = useState(false);
  const [bracketId, setBracketId] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referralCount, setReferralCount] = useState(0);
  const [referralLeaderboard, setReferralLeaderboard] = useState([]);
  const { user, isAuthenticated, token } = useAuth();
  const [searchParams] = useSearchParams();

  const isAdmin = user?.email === ADMIN_EMAIL;
  const flowMap = tournament ? buildFlowMap(tournament.games) : {};

  const fetchData = useCallback(async () => {
    try {
      const [tournamentRes, leaderboardRes, statsRes, refLeaderboardRes] = await Promise.allSettled([
        axios.get(`${API}/bracket/tournament`),
        axios.get(`${API}/bracket/leaderboard`),
        axios.get(`${API}/bracket/stats`),
        axios.get(`${API}/referral/leaderboard`)
      ]);

      if (tournamentRes.status === 'fulfilled') setTournament(tournamentRes.value.data);
      if (leaderboardRes.status === 'fulfilled') setLeaderboard(leaderboardRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (refLeaderboardRes.status === 'fulfilled') setReferralLeaderboard(refLeaderboardRes.value.data);

      if (isAuthenticated && token) {
        try {
          const [picksRes, refRes] = await Promise.allSettled([
            axios.get(`${API}/bracket/picks`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${API}/referral/code`, { headers: { Authorization: `Bearer ${token}` } })
          ]);
          if (picksRes.status === 'fulfilled' && picksRes.value.data?.picks) {
            const picksMap = {};
            picksRes.value.data.picks.forEach(p => { picksMap[p.game_id] = { seed: p.picked_seed, team: p.picked_team }; });
            setUserPicks(picksMap);
            setBracketId(picksRes.value.data.id);
          }
          if (refRes.status === 'fulfilled') {
            setReferralCode(refRes.value.data.referral_code);
            setReferralCount(refRes.value.data.referral_count);
          }
        } catch {}
      }
    } catch {} finally { setLoading(false); }
  }, [isAuthenticated, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle tip success/cancel from Stripe redirect
  useEffect(() => {
    if (searchParams.get('tip_success')) { toast.success('Thank you for the tip! You rock!'); }
    if (searchParams.get('tip_cancelled')) { toast.info('Tip cancelled - no worries!'); }
  }, [searchParams]);

  const shareLink = bracketId ? `${window.location.origin}/bracket/view/${bracketId}` : null;
  const ogShareLink = bracketId ? `${process.env.REACT_APP_BACKEND_URL}/api/bracket/og/${bracketId}` : null;
  const referralLink = referralCode ? `${window.location.origin}/register?ref=${referralCode}&redirect=/bracket` : null;

  const copyShareLink = () => {
    if (!ogShareLink) return;
    navigator.clipboard.writeText(ogShareLink);
    toast.success('Share link copied!');
  };

  const copyReferralLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied! Share it to earn rewards!');
  };

  const shareReferralToTwitter = () => {
    if (!referralLink) return;
    const text = encodeURIComponent(`Fill out your March Madness bracket on PrimeStatLab! Free to play, compete on the leaderboard! ${referralLink}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareToTwitter = () => {
    if (!ogShareLink) return;
    const text = encodeURIComponent(`Check out my March Madness bracket on PrimeStatLab! Think you can do better? ${ogShareLink}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const handlePick = (gameId, seed, team) => {
    if (!isAuthenticated) { toast.error('Please log in to make picks'); return; }
    if (tournament?.games?.some(g => g.is_complete)) { toast.error('Brackets are locked'); return; }
    setUserPicks(prev => {
      const newPicks = { ...prev };
      const oldPick = newPicks[gameId];
      newPicks[gameId] = { seed, team };
      // If pick changed, clear all downstream picks that depended on it
      if (!oldPick || oldPick.team !== team) {
        clearDownstream(gameId, newPicks, flowMap);
      }
      return newPicks;
    });
  };

  const saveBracket = async () => {
    if (!isAuthenticated) return;
    setSaving(true);
    try {
      const picks = Object.entries(userPicks).map(([gameId, pick]) => ({
        game_id: gameId, picked_seed: pick.seed, picked_team: pick.team
      }));
      const res = await axios.post(`${API}/bracket/picks`, { picks }, { headers: { Authorization: `Bearer ${token}` } });
      setBracketId(res.data.bracket_id);
      toast.success('Bracket saved!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTip = async (tipType) => {
    setTipping(true);
    try {
      const res = await axios.post(`${API}/bracket/tip`, {
        tip_type: tipType,
        origin_url: window.location.origin + '/bracket'
      });
      window.location.href = res.data.url;
    } catch (e) { toast.error('Tip failed - please try again'); setTipping(false); }
  };

  const handleAdminResult = async (gameId, winnerSeed, winnerName, team1Score, team2Score) => {
    try {
      await axios.put(`${API}/bracket/game/${gameId}/result`, {
        winner_seed: winnerSeed,
        winner_name: winnerName,
        team1_score: team1Score ? parseInt(team1Score) : null,
        team2_score: team2Score ? parseInt(team2Score) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${winnerName} advances!`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const [creatingTournament, setCreatingTournament] = useState(false);

  const handleCreateTournament = async () => {
    if (!isAdmin || !token) return;
    setCreatingTournament(true);
    try {
      // Step 1: Create tournament
      await axios.post(`${API}/bracket/tournament`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tournament created! Seeding teams...');
      
      // Step 2: Seed all 64 teams
      await axios.post(`${API}/bracket/tournament/teams/bulk`, {
        "East": {"1":"Duke","2":"Alabama","3":"Wisconsin","4":"Arizona","5":"Oregon","6":"BYU","7":"Saint Marys","8":"Mississippi St","9":"Creighton","10":"Vanderbilt","11":"VCU","12":"Liberty","13":"UNC Wilmington","14":"Lipscomb","15":"Robert Morris","16":"Norfolk St"},
        "West": {"1":"Florida","2":"St Johns","3":"Texas Tech","4":"Maryland","5":"Clemson","6":"Illinois","7":"UCLA","8":"Gonzaga","9":"Georgia","10":"Arkansas","11":"Drake","12":"UC San Diego","13":"Yale","14":"Troy","15":"Omaha","16":"Sacred Heart"},
        "South": {"1":"Auburn","2":"Michigan St","3":"Iowa St","4":"Texas A&M","5":"Michigan","6":"Missouri","7":"Kansas","8":"UConn","9":"Oklahoma","10":"New Mexico","11":"San Diego St","12":"Grand Canyon","13":"High Point","14":"Colgate","15":"Wofford","16":"Alabama St"},
        "Midwest": {"1":"Houston","2":"Tennessee","3":"Kentucky","4":"Purdue","5":"Marquette","6":"Louisville","7":"Baylor","8":"Pittsburgh","9":"Memphis","10":"Colorado St","11":"Xavier","12":"McNeese","13":"Akron","14":"Montana","15":"SIU Edwardsville","16":"SIUE"}
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('All 64 teams seeded! Refreshing...');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create tournament');
    } finally {
      setCreatingTournament(false);
    }
  };

  if (loading) return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-mono">Loading bracket...</div>
      </div>
    </Layout>
  );

  if (!tournament) return (
    <Layout>
      <Helmet><title>NCAA Bracket Challenge | PrimeStatLab</title></Helmet>
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <Trophy className="text-primary mb-6" size={80} />
        <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-center">NCAA Bracket Challenge</h1>
        <p className="text-muted-foreground text-lg mb-6">Coming Soon - Check back when the tournament begins!</p>
        {isAdmin && (
          <div className="text-center space-y-3" data-testid="admin-create-tournament">
            <p className="text-sm text-accent font-mono">Admin: No tournament found in database</p>
            <Button onClick={handleCreateTournament} disabled={creatingTournament} data-testid="create-tournament-btn"
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono uppercase tracking-wider px-8 py-4 text-base">
              {creatingTournament ? 'Creating & Seeding Teams...' : 'Create 2026 NCAA Tournament'}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );

  const regions = ['East', 'West', 'South', 'Midwest'];
  const isLocked = tournament.games.some(g => g.is_complete);

  return (
    <Layout>
      <Helmet>
        <title>NCAA Bracket Challenge | PrimeStatLab</title>
        <meta name="description" content="Fill out your NCAA Tournament bracket and compete. Free bracket challenge with leaderboard." />
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Hero */}
        <div className="text-center mb-10" data-testid="bracket-hero">
          <Trophy className="mx-auto text-primary mb-3" size={56} />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-3">MARCH MADNESS BRACKET</h1>
          <p className="text-base lg:text-lg text-muted-foreground mb-6">{tournament.name} - Fill out your bracket and compete!</p>
          
          <div className="flex justify-center gap-10 mb-6">
            <div className="text-center">
              <Users className="mx-auto text-primary mb-1" size={22} />
              <div className="font-mono text-2xl font-bold">{stats.total_brackets}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Brackets</div>
            </div>
            <div className="text-center">
              <Target className="mx-auto text-accent mb-1" size={22} />
              <div className="font-mono text-2xl font-bold">{stats.games_complete}/{stats.total_games}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Games</div>
            </div>
          </div>

          {isLocked && (
            <div className="inline-flex items-center gap-2 bg-loss/20 text-loss px-4 py-2 rounded" data-testid="brackets-locked">
              <Lock size={14} />
              <span className="font-mono text-sm">BRACKETS LOCKED</span>
            </div>
          )}
        </div>

        {/* $500 Bracket Prize + $100 Referral Banner */}
        <div className="mb-10 bg-gradient-to-r from-accent/10 via-primary/5 to-accent/10 border-2 border-accent/40 rounded-xl p-6 text-center max-w-2xl mx-auto" data-testid="prize-banner">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <div className="text-center">
              <div className="font-mono text-4xl sm:text-5xl font-black text-accent">$500</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Top Bracket</div>
            </div>
            <div className="text-2xl text-muted-foreground font-thin">+</div>
            <div className="text-center">
              <div className="font-mono text-4xl sm:text-5xl font-black text-primary">$100</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Top Referrer</div>
            </div>
            <div className="text-2xl text-muted-foreground font-thin">=</div>
            <div className="text-center">
              <div className="font-mono text-4xl sm:text-5xl font-black text-foreground">$600</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Total Prizes</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4 font-mono">FREE TO PLAY. FILL OUT YOUR BRACKET. WIN CASH.</p>
        </div>

        {/* Region Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="region-tabs">
          {regions.map(region => (
            <button key={region} onClick={() => setActiveRegion(region)} data-testid={`region-tab-${region.toLowerCase()}`}
              className={`px-5 py-2.5 font-mono text-sm uppercase tracking-wider rounded transition-all ${
                activeRegion === region ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary/50'
              }`}>{region}</button>
          ))}
          <button onClick={() => setActiveRegion('Final Four')} data-testid="region-tab-final-four"
            className={`px-5 py-2.5 font-mono text-sm uppercase tracking-wider rounded transition-all ${
              activeRegion === 'Final Four' ? 'bg-accent text-accent-foreground' : 'bg-card border border-border hover:border-accent/50'
            }`}>Final Four</button>
        </div>

        {/* Bracket Display */}
        {activeRegion !== 'Final Four' ? (
          <RegionBracket
            tournament={tournament}
            region={activeRegion}
            userPicks={userPicks}
            flowMap={flowMap}
            onPick={handlePick}
            isLocked={isLocked}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin && showAdmin}
            onAdminResult={handleAdminResult}
          />
        ) : (
          <FinalFourBracket
            tournament={tournament}
            userPicks={userPicks}
            flowMap={flowMap}
            onPick={handlePick}
            isLocked={isLocked}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin && showAdmin}
            onAdminResult={handleAdminResult}
          />
        )}

        {/* Save / Share / Login CTA */}
        {isAuthenticated && !isLocked && Object.keys(userPicks).length > 0 && (
          <div className="text-center mt-8 space-y-4">
            <Button onClick={saveBracket} disabled={saving} data-testid="save-bracket-btn"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono uppercase tracking-wider px-10 py-4 text-base">
              {saving ? 'Saving...' : 'Save My Bracket'}
            </Button>
          </div>
        )}

        {/* Share buttons - show when user has a saved bracket */}
        {isAuthenticated && bracketId && (
          <div className="text-center mt-4 p-5 bg-card border border-border rounded-lg max-w-md mx-auto" data-testid="share-bracket-section">
            <Share2 className="mx-auto text-primary mb-2" size={20} />
            <p className="text-sm text-muted-foreground mb-3">Challenge your friends!</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={copyShareLink} className="font-mono text-xs uppercase gap-1.5" data-testid="copy-bracket-link">
                <Copy size={13} /> Copy Link
              </Button>
              <Button variant="outline" onClick={shareToTwitter} className="font-mono text-xs uppercase gap-1.5" data-testid="share-bracket-twitter">
                Share on X
              </Button>
            </div>
          </div>
        )}

        {!isAuthenticated && (
          <div className="text-center mt-8 p-8 bg-card border border-border rounded-lg max-w-md mx-auto" data-testid="login-prompt">
            <Lock className="mx-auto text-muted-foreground mb-3" size={28} />
            <p className="text-muted-foreground mb-4">Log in to fill out your bracket and compete!</p>
            <Link to="/login"><Button className="bg-primary hover:bg-primary/90">Log In</Button></Link>
          </div>
        )}

        {/* Admin Toggle */}
        {isAdmin && (
          <div className="text-center mt-6">
            <Button variant="outline" onClick={() => setShowAdmin(!showAdmin)} data-testid="admin-toggle-btn"
              className="font-mono text-xs uppercase gap-2">
              <Shield size={14} />
              {showAdmin ? 'Hide Admin Controls' : 'Show Admin Controls'}
            </Button>
          </div>
        )}

        {/* $100 Referral Contest */}
        <div className="mt-16 bg-gradient-to-b from-accent/10 to-transparent border-2 border-accent/40 rounded-xl p-8 text-center max-w-3xl mx-auto" data-testid="referral-contest">
          <div className="inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-1.5 rounded-full mb-4">
            <Gift size={16} />
            <span className="font-mono text-xs uppercase tracking-widest font-bold">$100 Prize</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-3">SHARE & WIN $100</h2>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto mb-6 leading-relaxed">
            The person who refers the most new bracket players by the end of the tournament wins <span className="text-accent font-bold">$100 cash</span>! 
            Share your unique link, get friends to sign up & play, and climb the referral leaderboard.
          </p>

          {isAuthenticated && referralCode ? (
            <div className="space-y-4" data-testid="referral-share-area">
              <div className="bg-background border border-border rounded-lg p-3 max-w-sm mx-auto">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Your Referral Code</p>
                <p className="font-mono text-lg font-bold text-primary" data-testid="referral-code">{referralCode}</p>
              </div>
              <div className="flex items-center gap-2 justify-center text-sm">
                <Star size={14} className="text-accent" />
                <span className="font-mono">You've referred <span className="text-accent font-bold" data-testid="referral-count">{referralCount}</span> {referralCount === 1 ? 'person' : 'people'}</span>
              </div>
              <div className="flex justify-center gap-2">
                <Button onClick={copyReferralLink} className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono text-xs uppercase gap-1.5" data-testid="copy-referral-link">
                  <Copy size={13} /> Copy Referral Link
                </Button>
                <Button variant="outline" onClick={shareReferralToTwitter} className="font-mono text-xs uppercase gap-1.5" data-testid="share-referral-twitter">
                  Share on X
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-4">Sign up or log in to get your unique referral link!</p>
              <Link to="/register?redirect=/bracket">
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono uppercase tracking-wider">
                  Sign Up & Get Your Link
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Referral Leaderboard */}
        <div className="mt-10" data-testid="referral-leaderboard-section">
          <h2 className="text-xl font-heading font-bold text-center mb-6 flex items-center justify-center gap-2">
            <Gift size={20} className="text-accent" /> REFERRAL LEADERBOARD <span className="text-accent font-mono text-sm">($100 Prize)</span>
          </h2>
          
          {referralLeaderboard.length === 0 ? (
            <p className="text-center text-muted-foreground font-mono text-sm">No referrals yet. Be the first to share and win!</p>
          ) : (
            <div className="max-w-lg mx-auto bg-card border border-accent/30 rounded-lg overflow-hidden">
              <div className="bg-accent/10 px-4 py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground grid grid-cols-3 border-b border-border">
                <span>Rank</span><span>Player</span><span className="text-right">Referrals</span>
              </div>
              {referralLeaderboard.map((entry, i) => (
                <div key={entry.user_id} data-testid={`referral-entry-${i}`}
                  className={`px-4 py-3 grid grid-cols-3 items-center border-t border-border/50 ${i === 0 ? 'bg-accent/10' : ''} ${entry.user_id === user?.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}>
                  <span className={`font-mono font-bold ${i === 0 ? 'text-accent' : ''}`}>
                    {i === 0 ? '👑' : `#${entry.rank}`}
                  </span>
                  <span className="font-mono text-sm">
                    {entry.username}
                    {entry.user_id === user?.id && <span className="text-primary ml-1">(You)</span>}
                  </span>
                  <span className="text-right font-mono font-bold text-accent">{entry.referrals}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* For The Fans + Tip Jar */}
        <div className="mt-16 bg-gradient-to-b from-primary/5 to-transparent border border-primary/20 rounded-lg p-8 text-center max-w-3xl mx-auto" data-testid="for-the-fans">
          <Heart className="mx-auto text-loss mb-4" size={40} />
          <h2 className="text-xl font-heading font-bold mb-4">FOR THE FANS</h2>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-6 leading-relaxed">
            This bracket challenge is my gift to you - the real fans who live and breathe March Madness. 
            No ads, no gimmicks. Fill out your bracket, compete with friends, and enjoy the madness. This is for YOU.
          </p>
          <p className="text-xs text-muted-foreground italic mb-6">- PrimeStatLab</p>
          
          <div className="border-t border-border pt-6">
            <p className="text-xs text-muted-foreground mb-4 uppercase tracking-wider">Support the lab</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { type: 'coffee', label: 'Coffee', amount: '$3', icon: '☕' },
                { type: 'lunch', label: 'Lunch', amount: '$10', icon: '🍔' },
                { type: 'big_tip', label: 'Big Tip', amount: '$25', icon: '🔥' },
              ].map(tip => (
                <button key={tip.type} onClick={() => handleTip(tip.type)} disabled={tipping} data-testid={`tip-${tip.type}`}
                  className="bg-card border border-border hover:border-primary/50 rounded-lg px-5 py-3 transition-all hover:bg-primary/5 disabled:opacity-50">
                  <span className="block text-lg mb-1">{tip.icon}</span>
                  <span className="block font-mono text-xs">{tip.amount}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bracket Leaderboard */}
        <div className="mt-16" data-testid="leaderboard-section">
          <h2 className="text-2xl font-heading font-bold text-center mb-8 flex items-center justify-center gap-2">
            <Trophy size={24} className="text-accent" /> BRACKET LEADERBOARD
          </h2>
          
          {leaderboard.length === 0 ? (
            <p className="text-center text-muted-foreground font-mono text-sm">No brackets submitted yet. Be the first!</p>
          ) : (
            <div className="max-w-2xl mx-auto bg-card border border-border rounded-lg overflow-hidden">
              <div className="bg-card/80 px-4 py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground grid grid-cols-4 border-b border-border">
                <span>Rank</span><span>Player</span><span className="text-right">Points</span><span className="text-right">Correct</span>
              </div>
              {leaderboard.slice(0, 20).map((entry, i) => (
                <div key={entry.user_id} data-testid={`leaderboard-entry-${i}`}
                  className={`px-4 py-3 grid grid-cols-4 items-center border-t border-border/50 ${i < 3 ? 'bg-primary/5' : ''} ${entry.user_id === user?.id ? 'bg-accent/10 border-l-2 border-l-accent' : ''}`}>
                  <span className={`font-mono font-bold ${i === 0 ? 'text-accent' : ''}`}>#{entry.rank}</span>
                  <span className="font-mono text-sm">{entry.username}{entry.user_id === user?.id && <span className="text-accent ml-1">(You)</span>}</span>
                  <span className="text-right font-mono font-bold text-primary">{entry.points}</span>
                  <span className="text-right font-mono text-sm text-muted-foreground">{entry.correct_picks}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

/* ==================== Region Bracket ==================== */
function RegionBracket({ tournament, region, userPicks, flowMap, onPick, isLocked, isAuthenticated, isAdmin, onAdminResult }) {
  const regionGames = tournament.games.filter(g => g.region === region);
  const rounds = [2, 3, 4, 5];

  return (
    <div className="overflow-x-auto pb-4" data-testid={`bracket-region-${region.toLowerCase()}`}>
      <div className="min-w-[800px]">
        <div className="flex gap-3">
          {rounds.map(round => {
            const games = regionGames.filter(g => g.round_num === round);
            const spacing = round === 2 ? 'gap-1' : round === 3 ? 'gap-6' : round === 4 ? 'gap-20' : 'gap-0';
            return (
              <div key={round} className="flex-1">
                <div className="text-center mb-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {ROUND_NAMES[round]}
                  </span>
                  <span className="font-mono text-[10px] text-primary ml-1">({ROUND_POINTS[round]}pt)</span>
                </div>
                <div className={`flex flex-col justify-around h-full ${spacing}`}>
                  {games.map(game => {
                    const vt = getVirtualTeams(game, userPicks, flowMap);
                    return (
                      <GameCard key={game.game_id} game={game} userPick={userPicks[game.game_id]}
                        vTeam1={vt.t1} vTeam2={vt.t2}
                        onPick={onPick} isLocked={isLocked} isAuthenticated={isAuthenticated}
                        isAdmin={isAdmin} onAdminResult={onAdminResult} />
                    );
                  })}
                </div>
              </div>
            );
          })}
          {/* Region Champion */}
          <div className="flex-shrink-0 flex items-center">
            <div className="text-center">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block mb-2">
                {region} Champ
              </span>
              <div className="bg-primary/10 border-2 border-primary/50 rounded-lg px-4 py-3 min-w-[110px]">
                <span className="font-mono text-sm font-bold">
                  {regionGames.find(g => g.round_num === 5)?.winner_name ||
                   userPicks[regionGames.find(g => g.round_num === 5)?.game_id]?.team ||
                   '???'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================== Final Four Bracket ==================== */
function FinalFourBracket({ tournament, userPicks, flowMap, onPick, isLocked, isAuthenticated, isAdmin, onAdminResult }) {
  const ffGames = tournament.games.filter(g => g.round_num === 6);
  const champGame = tournament.games.find(g => g.round_num === 7);

  return (
    <div className="max-w-3xl mx-auto" data-testid="bracket-final-four">
      <div className="grid grid-cols-3 gap-6 items-center">
        <div className="space-y-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-center text-muted-foreground">
            Final Four <span className="text-primary">({ROUND_POINTS[6]}pt)</span>
          </div>
          {ffGames.map(game => {
            const vt = getVirtualTeams(game, userPicks, flowMap);
            return (
              <GameCard key={game.game_id} game={game} userPick={userPicks[game.game_id]}
                vTeam1={vt.t1} vTeam2={vt.t2}
                onPick={onPick} isLocked={isLocked} isAuthenticated={isAuthenticated}
                isAdmin={isAdmin} onAdminResult={onAdminResult} />
            );
          })}
        </div>

        <div className="text-center space-y-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
            Championship <span>({ROUND_POINTS[7]}pt)</span>
          </div>
          {champGame && (() => {
            const vt = getVirtualTeams(champGame, userPicks, flowMap);
            return (
              <GameCard game={champGame} userPick={userPicks[champGame.game_id]}
                vTeam1={vt.t1} vTeam2={vt.t2}
                onPick={onPick} isLocked={isLocked} isAuthenticated={isAuthenticated}
                isAdmin={isAdmin} onAdminResult={onAdminResult} isChampionship />
            );
          })()}
          <div className="pt-2">
            <Trophy className="mx-auto text-accent mb-1" size={28} />
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Champion</div>
            <div className="bg-accent/10 border-2 border-accent/50 rounded-lg p-3 mt-1">
              <span className="font-heading text-lg font-bold">
                {champGame?.winner_name || userPicks[champGame?.game_id]?.team || '???'}
              </span>
            </div>
          </div>
        </div>

        <div />
      </div>
    </div>
  );
}

/* ==================== Game Card ==================== */
function GameCard({ game, userPick, vTeam1, vTeam2, onPick, isLocked, isAuthenticated, isAdmin, onAdminResult, isChampionship }) {
  const [adminMode, setAdminMode] = useState(false);
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');

  // Use virtual teams (from cascaded picks) or actual game teams
  const t1Name = vTeam1?.name || game.team1_name;
  const t1Seed = vTeam1?.seed || game.team1_seed;
  const t2Name = vTeam2?.name || game.team2_name;
  const t2Seed = vTeam2?.seed || game.team2_seed;

  const team1Selected = userPick?.team === t1Name;
  const team2Selected = userPick?.team === t2Name;
  const hasWinner = game.is_complete;
  const isVirtual1 = !game.team1_name && t1Name; // team came from user's pick cascade
  const isVirtual2 = !game.team2_name && t2Name;

  return (
    <div className={`rounded-lg overflow-hidden border ${isChampionship ? 'border-accent/50' : 'border-border'} ${hasWinner ? 'ring-1 ring-accent/30' : ''}`}
      data-testid={`game-card-${game.game_id}`}>
      <TeamRow team={t1Name} seed={t1Seed} score={game.team1_score}
        isWinner={hasWinner && game.winner_name === t1Name}
        isLoser={hasWinner && game.winner_name !== t1Name && game.winner_name}
        isSelected={team1Selected && !hasWinner}
        isVirtual={isVirtual1}
        onClick={() => t1Name && onPick(game.game_id, t1Seed, t1Name)}
        disabled={isLocked || !isAuthenticated || !t1Name} />
      <div className="border-t border-border/50" />
      <TeamRow team={t2Name} seed={t2Seed} score={game.team2_score}
        isWinner={hasWinner && game.winner_name === t2Name}
        isLoser={hasWinner && game.winner_name !== t2Name && game.winner_name}
        isSelected={team2Selected && !hasWinner}
        isVirtual={isVirtual2}
        onClick={() => t2Name && onPick(game.game_id, t2Seed, t2Name)}
        disabled={isLocked || !isAuthenticated || !t2Name} />
      
      {/* Admin result entry - only when admin toggle is on */}
      {isAdmin && !hasWinner && t1Name && t2Name && (
        <>
          {!adminMode ? (
            <button onClick={() => setAdminMode(true)} data-testid={`admin-enter-result-${game.game_id}`}
              className="w-full py-1 bg-accent/10 text-accent text-[10px] font-mono uppercase hover:bg-accent/20 transition-colors">
              Enter Result
            </button>
          ) : (
            <div className="bg-card/80 p-2 space-y-1.5" data-testid={`admin-result-form-${game.game_id}`}>
              <div className="flex gap-1.5">
                <input type="number" placeholder="Score" value={score1} onChange={e => setScore1(e.target.value)}
                  className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs font-mono w-12" />
                <input type="number" placeholder="Score" value={score2} onChange={e => setScore2(e.target.value)}
                  className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs font-mono w-12" />
              </div>
              <div className="flex gap-1">
                <button onClick={() => { onAdminResult(game.game_id, t1Seed, t1Name, score1, score2); setAdminMode(false); }}
                  className="flex-1 bg-primary/20 text-primary text-[10px] font-mono py-1 rounded hover:bg-primary/30" data-testid={`admin-pick-team1-${game.game_id}`}>
                  {t1Name?.substring(0, 8)} wins
                </button>
                <button onClick={() => { onAdminResult(game.game_id, t2Seed, t2Name, score1, score2); setAdminMode(false); }}
                  className="flex-1 bg-primary/20 text-primary text-[10px] font-mono py-1 rounded hover:bg-primary/30" data-testid={`admin-pick-team2-${game.game_id}`}>
                  {t2Name?.substring(0, 8)} wins
                </button>
              </div>
              <button onClick={() => setAdminMode(false)} className="w-full text-[10px] text-muted-foreground hover:text-foreground">Cancel</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ==================== Team Row ==================== */
function TeamRow({ team, seed, score, isWinner, isLoser, isSelected, isVirtual, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`w-full px-2.5 py-1.5 flex items-center justify-between transition-all text-left ${
        isWinner ? 'bg-accent/15 text-accent' :
        isLoser ? 'opacity-40 line-through' :
        isSelected ? 'bg-primary/15 text-primary' :
        isVirtual ? 'bg-primary/5 text-primary/70' :
        'hover:bg-card/80'
      } ${!team ? 'opacity-25' : ''} disabled:cursor-default`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="font-mono text-[10px] text-muted-foreground w-4 flex-shrink-0 text-right">{seed || '-'}</span>
        <span className={`font-mono text-xs truncate ${isVirtual ? 'italic' : ''}`}>{team || 'TBD'}</span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isSelected && !isWinner && <Check size={12} className="text-primary" />}
        {isWinner && <Check size={12} className="text-accent" />}
        {score !== null && score !== undefined && <span className="font-mono text-[10px] ml-1">{score}</span>}
      </div>
    </button>
  );
}
